import React, { Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Table, TableBody, TableContainer, TableRow } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { getSharedStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import { makeTree } from 'app/shared/components/common/Tree';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { mixed } from 'yup';

import { PAGE_CODES } from 'constants/pageCodes';
import useForm from 'hooks/useForm';
import { getScrollStyles } from 'styles/scrollbar.styles';
import { findTreeItemParentIds } from 'utils/tree';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { showMessageAction } from 'store/global/actions';
import {
  changeApplicationChecklistResultAction,
  showMessageThatCurrentUserCantChangeLoanAction,
  toggleEditStateAction,
} from 'store/loanApplications/actions';
import {
  selectIsCurrentUserCanChangeLoan,
  selectIsLoanCanceled,
} from 'store/loanApplications/selectors';
import { selectExtendedUsers } from 'store/users/selectors';

import {
  getOverrideGroupsOutputValues,
  sortChecklistsTreeItems,
} from './ChecklistItemsTable.utils';
import ChecklistItemsTableControls from './ChecklistItemsTableControls';
import ChecklistItemsTableHead from './ChecklistItemsTableHead';
import ChecklistItemsTableRow from './ChecklistItemsTableRow';

const getStyles = theme => ({
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...getScrollStyles(theme).scroll,
  },
  table: {
    minWidth: 600,
    border: `1px solid ${theme.palette.divider}`,
    '& th': { backgroundColor: theme.palette.primary['300'] },
    '& th:nth-child(1)': { width: '40%' }, // Item Name
    '& th:nth-child(2)': { width: '12%' }, // Determination
    '& th:nth-child(3)': { width: '12%' }, // Result
    '& th:nth-child(4)': { width: '36%' }, // Override (checkbox + supervisor + date)
    '& [data-is-field-changed="true"]': {
      color: theme.palette.primary.main,
      '& .MuiCheckbox-root': {
        color: theme.palette.primary.main,
      },
    },
  },
  nestedTableRowCells: {
    '& td': { borderBottom: '1px solid transparent' },
  },
});

const checklistItemsSchema = mixed().nullable();

const ChecklistItemsTable = ({
  isLoading,
  setIsLoading,
  isEditMode,
  tableData,
  tableId,
  checklistId,
  applicationId,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useMemo(() => getStyles(theme), [theme]);
  const sharedClasses = useMemo(() => getSharedStyles(theme), [theme]);

  const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);
  const { isManageAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_CHECKLISTS));
  const isLoanCancelled = useSelector(selectIsLoanCanceled);
  const users = useSelector(selectExtendedUsers);

  const isEditAllowed = isManageAllowed && !!tableData?.length && !isLoanCancelled;

  const { treeItemsValues, treeData } = useMemo(() => {
    const treeItemsValues = _.keyBy(tableData ?? [], 'id');
    const treeData = makeTree(treeItemsValues, sortChecklistsTreeItems);

    return { treeItemsValues, treeData };
  }, [tableData]);

  const initialFormValues = useMemo(
    () =>
      tableData?.reduce((acc, { id, calculations }) => {
        if (calculations) {
          acc[id] = !!calculations.wasOverriden;
        }

        return acc;
      }, {}),
    [tableData],
  );

  const { form, handleChange, setInitialFormValues, isFormChanged, resetForm, setSchema } =
    useForm(initialFormValues);

  useEffect(() => {
    if (!isEditAllowed) {
      return;
    }

    setSchema(checklistItemsSchema);
  }, [isEditAllowed, setSchema]);

  useEffect(() => {
    setInitialFormValues(initialFormValues);
  }, [initialFormValues, setInitialFormValues]);

  const onToggleEditMode = useCallback(() => {
    if (!isCurrentUserCanChangeLoan) {
      return dispatch(showMessageThatCurrentUserCantChangeLoanAction());
    }

    resetForm();

    dispatch(toggleEditStateAction(tableId));
  }, [dispatch, isCurrentUserCanChangeLoan, resetForm, tableId]);

  const onFormSubmit = useCallback(
    e => {
      e.preventDefault();
      // Form validation is omitted consciously

      const overrideGroups = getOverrideGroupsOutputValues(form, initialFormValues);

      setIsLoading(true);

      dispatch(
        changeApplicationChecklistResultAction({
          ...overrideGroups,
          checklistId,
          applicationId,
          tabId: LoanApplicationTabId.CHECKLISTS,
        }),
      )
        .then(() => {
          const message = 'Override was successfully applied';
          dispatch(showMessageAction({ message, variant: 'success' }));
          dispatch(toggleEditStateAction(tableId));
        })
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [form, initialFormValues, setIsLoading, dispatch, checklistId, applicationId, tableId],
  );

  return (
    <TableContainer component="form" sx={classes.tableContainer} onSubmit={onFormSubmit}>
      {isEditAllowed && (
        <ChecklistItemsTableControls
          isEditMode={isEditMode}
          isFormChanged={isFormChanged}
          isLoading={isLoading}
          onToggleEditMode={onToggleEditMode}
        />
      )}

      <Table stickyHeader size="small" sx={classes.table}>
        <ChecklistItemsTableHead />

        <TableBody>
          {treeData.map(function renderTreeItems(checklistItem, index) {
            const { id, nestedFields, parentId } = checklistItem;
            const parentIds = findTreeItemParentIds({ id, treeData: treeItemsValues });
            const rootIndex = parentId ? _.findIndex(treeData, { id: parentIds.at(-1) }) : index;

            const itemLevel = parentIds.length;
            const isEvenGroup = rootIndex % 2;
            const isSingleParent = !parentId && !nestedFields?.length;

            return (
              <Fragment key={id}>
                <TableRow
                  className={clsx({
                    [isSingleParent ? 'h-64' : 'h-52']: true,
                    [sharedClasses.evenGroup]: isEvenGroup,
                  })}
                  data-testid={id}
                  sx={!isSingleParent ? classes.nestedTableRowCells : undefined}
                >
                  <ChecklistItemsTableRow
                    checklistItem={checklistItem}
                    handleChange={handleChange}
                    isEditMode={isEditMode}
                    isOverride={form[id]}
                    itemLevel={itemLevel}
                    users={users}
                  />
                </TableRow>

                {nestedFields?.map(renderTreeItems)}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

ChecklistItemsTable.propTypes = {
  ...WithLoadingPropTypes,
  isEditMode: PropTypes.bool.isRequired,
  tableData: PropTypes.array.isRequired,
  tableId: PropTypes.string.isRequired,
  checklistId: PropTypes.string.isRequired,
  applicationId: PropTypes.string.isRequired,
};

export default compose(withLoading, memo)(ChecklistItemsTable);
